import { useState } from "react";
import { Card } from "@tellescope/design-system";
import { useTranslation } from "react-i18next";
import type { FormResponse } from "@tellescope/types-models";
import type {
  FormResponseListBlock as FormResponseListBlockType,
  FormResponseCategory,
} from "../../../../shared/schema";
import { MobileList } from "../../MobileList";
import { useEnduserFormResponses } from "../../../hooks/useEnduserFormResponses";
import { useFormResponsesFilter } from "../../../hooks/useItemFilter";
import { useEnduserPinnedForms } from "../../../hooks/useEnduserPinnedForms";
import { FormResponseDialog } from "../FormResponseDialog";
import { NativeFormResponseDialog } from "../NativeFormResponseDialog";
import { useIsNewFormsExperienceEnabled } from "../../../../shared/store/portalSchemaStore";
import { invalidateFormResponseQueries } from "../../enduserQueryClient";
import { FormResponseCard } from "./FormResponseCard";
import { FormResponseListSkeleton } from "./FormResponseListSkeleton";
import { CategorySelector } from "./CategorySelector";

interface FormResponseListBlockProps {
  block: FormResponseListBlockType;
}

export const FormResponseListBlock = ({ block }: FormResponseListBlockProps) => {
  const { t } = useTranslation("forms");
  const categories = block.props.categories ?? ["all"];
  const { itemFilter, titleOverrides, hideUnsubmittedAfter, hideUnsubmittedFormRules } = block.props;
  const showCategorySelector = categories.length > 1;

  const [selectedCategory, setSelectedCategory] = useState<FormResponseCategory>(categories[0] ?? "all");
  const [selectedFormResponse, setSelectedFormResponse] = useState<FormResponse | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const isNewFormsExperience = useIsNewFormsExperienceEnabled();

  const { pinnedOutstandingResponseIds } = useEnduserPinnedForms();

  const {
    formResponses: rawFormResponses,
    isLoading: responsesLoading,
    error,
  } = useEnduserFormResponses({
    category: selectedCategory,
    excludeResponseIds: pinnedOutstandingResponseIds,
    hideUnsubmittedAfter,
    hideUnsubmittedFormRules,
  });

  const { filteredItems: formResponses, isLoading: filterLoading } = useFormResponsesFilter({
    items: rawFormResponses,
    itemFilter,
  });

  const isLoading = responsesLoading || filterLoading;

  const handleFormResponseClick = (formResponse: FormResponse) => {
    setSelectedFormResponse(formResponse);
    setDialogOpen(true);
  };

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      invalidateFormResponseQueries();
      setTimeout(() => setSelectedFormResponse(null), 200);
    }
  };

  const getModalTitle = () => {
    switch (selectedCategory) {
      case "submitted":
        return t("Submitted Forms");
      case "unsubmitted":
        return t("Pending Forms");
      case "all":
      default:
        return t("All Forms");
    }
  };

  const getDefaultEmptyMessage = () => {
    switch (selectedCategory) {
      case "submitted":
        return t("No submitted forms");
      case "unsubmitted":
        return t("No pending forms");
      case "all":
      default:
        return t("No forms");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {showCategorySelector && (
          <CategorySelector
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
          />
        )}
        <FormResponseListSkeleton count={block.props.maxItems ?? 5} />
      </div>
    );
  }

  if (error) {
    return (
      <Card variant="floating" padding="root" className="border-destructive">
        <Card.Body className="p-4 text-center text-destructive">{t("Failed to load forms")}</Card.Body>
      </Card>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {showCategorySelector && (
          <CategorySelector
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
          />
        )}
        <MobileList
          items={formResponses}
          renderItem={(formResponse) => (
            <FormResponseCard
              formResponse={formResponse}
              titleOverrides={titleOverrides}
              onClick={() => handleFormResponseClick(formResponse)}
            />
          )}
          title={getModalTitle()}
          maxItems={block.props.maxItems ?? 5}
          emptyMessage={block.props.emptyMessage ?? getDefaultEmptyMessage()}
        />
      </div>

      {isNewFormsExperience(selectedFormResponse?.formId) ? (
        <NativeFormResponseDialog
          formResponse={selectedFormResponse}
          titleOverride={selectedFormResponse ? titleOverrides?.[selectedFormResponse.formTitle] : undefined}
          open={dialogOpen}
          onOpenChange={handleDialogClose}
        />
      ) : (
        <FormResponseDialog
          formResponse={selectedFormResponse}
          titleOverride={selectedFormResponse ? titleOverrides?.[selectedFormResponse.formTitle] : undefined}
          open={dialogOpen}
          onOpenChange={handleDialogClose}
        />
      )}
    </>
  );
};
